import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Star, Mail, Phone, FileText, Building, Pencil, Trash2, Save, Loader2 } from 'lucide-react';
import { api, AED } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';

const TRADES = [
  'General Contracting', 'MEP', 'Joinery', 'Tiling & Finishes',
  'Painting', 'HVAC', 'Plumbing', 'Electrical', 'Sanitary', 'Landscaping',
];

export default function VendorModal({ vendorId, onClose, onOpenInvoice, onChanged }) {
  const { user: me } = useAuth();
  const isAdmin = !!me && ['owner', 'md'].includes(me.role);
  const [v, setV] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [boqs, setBoqs] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => {
    if (!vendorId) return;
    return Promise.all([
      api.get(`/vendors/${vendorId}`),
      api.get('/invoices', { params: { vendor_id: vendorId } }),
      api.get('/boqs'),
    ]).then(([vr, ir, br]) => {
      setV(vr.data);
      setInvoices(ir.data);
      setBoqs(br.data.filter(b => b.vendor_id === vendorId));
    });
  };
  useEffect(() => { load(); }, [vendorId]);

  const startEdit = () => {
    setForm({
      name: v.name || '', company: v.company || '', trade: v.trade || 'General Contracting',
      email: v.email || '', phone: v.phone || '', trn: v.trn || '',
      trade_license: v.trade_license || '', iban: v.iban || '', bank_name: v.bank_name || '',
      notes: v.notes || '',
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.patch(`/vendors/${vendorId}`, form);
      toast.success('Vendor updated');
      setEditing(false);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    } finally { setBusy(false); }
  };

  const doDelete = async () => {
    const msg = `Delete "${v.company || v.name}"?\n\nThis vendor will be permanently removed. `
      + `If they have any open BOQs, invoices or POs the delete will be blocked — you must reassign or remove those first.\n\nThis cannot be undone.`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      await api.delete(`/vendors/${vendorId}`);
      toast.success('Vendor deleted');
      onChanged?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    } finally { setBusy(false); }
  };

  if (!v) return null;

  const paid = invoices.filter(i => i.status === 'paid').reduce((s,i)=>s+(i.net_payable_aed||0),0);
  const pending = invoices.filter(i => i.status?.startsWith('pending')).reduce((s,i)=>s+(i.net_payable_aed||0),0);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} data-testid="vendor-modal">
      <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} onClick={(e)=>e.stopPropagation()}
                  className="glass-strong w-full max-w-3xl max-h-[90vh] overflow-y-auto p-8">
        <div className="flex items-start justify-between mb-6">
          <div className="flex-1">
            {editing ? (
              <>
                <div className="overline">Editing vendor</div>
                <input value={form.company} onChange={(e)=>setForm({...form, company:e.target.value})}
                       placeholder="Company / vendor name"
                       data-testid="vm-edit-company"
                       className="font-serif text-3xl bg-transparent border-b border-slate-300 focus:border-slate-900 outline-none w-full mt-1"/>
              </>
            ) : (
              <>
                <div className="overline">Vendor</div>
                <div className="font-serif text-3xl">{v.company}</div>
                <div className="text-sm text-slate-500 mt-1">{v.name} · {v.trade}</div>
              </>
            )}
          </div>
          <div className="flex items-center gap-1 ml-3">
            {!editing && isAdmin && (
              <>
                <button onClick={startEdit} data-testid="vm-edit-btn"
                        title="Edit vendor" className="p-2 hover:bg-white/60 rounded-full">
                  <Pencil size={16}/>
                </button>
                <button onClick={doDelete} disabled={busy} data-testid="vm-delete-btn"
                        title="Delete vendor" className="p-2 hover:bg-red-50 rounded-full text-red-600">
                  <Trash2 size={16}/>
                </button>
              </>
            )}
            <button onClick={onClose} className="p-2 hover:bg-white/60 rounded-full"><X size={18}/></button>
          </div>
        </div>

        {editing ? (
          <div className="glass p-4 mb-6 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="text-xs uppercase tracking-widest text-slate-500">
                Contact person
                <input value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})}
                       data-testid="vm-edit-name"
                       className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 bg-white/80 text-sm normal-case tracking-normal"/>
              </label>
              <label className="text-xs uppercase tracking-widest text-slate-500">
                Trade
                <select value={form.trade} onChange={(e)=>setForm({...form, trade:e.target.value})}
                        className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 bg-white/80 text-sm normal-case tracking-normal">
                  {TRADES.map(t => <option key={t}>{t}</option>)}
                </select>
              </label>
              <label className="text-xs uppercase tracking-widest text-slate-500">
                Email
                <input value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})}
                       type="email" data-testid="vm-edit-email"
                       className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 bg-white/80 text-sm normal-case tracking-normal"/>
              </label>
              <label className="text-xs uppercase tracking-widest text-slate-500">
                Phone
                <input value={form.phone} onChange={(e)=>setForm({...form, phone:e.target.value})}
                       className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 bg-white/80 text-sm normal-case tracking-normal"/>
              </label>
              <label className="text-xs uppercase tracking-widest text-slate-500">
                TRN
                <input value={form.trn} onChange={(e)=>setForm({...form, trn:e.target.value})}
                       className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 bg-white/80 text-sm font-mono normal-case tracking-normal"/>
              </label>
              <label className="text-xs uppercase tracking-widest text-slate-500">
                Trade licence
                <input value={form.trade_license} onChange={(e)=>setForm({...form, trade_license:e.target.value})}
                       className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 bg-white/80 text-sm font-mono normal-case tracking-normal"/>
              </label>
              <label className="text-xs uppercase tracking-widest text-slate-500">
                Bank name
                <input value={form.bank_name} onChange={(e)=>setForm({...form, bank_name:e.target.value})}
                       className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 bg-white/80 text-sm normal-case tracking-normal"/>
              </label>
              <label className="text-xs uppercase tracking-widest text-slate-500">
                IBAN
                <input value={form.iban} onChange={(e)=>setForm({...form, iban:e.target.value})}
                       className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 bg-white/80 text-sm font-mono normal-case tracking-normal"/>
              </label>
            </div>
            <label className="text-xs uppercase tracking-widest text-slate-500 block">
              Notes
              <textarea value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})}
                        rows={3}
                        className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 bg-white/80 text-sm normal-case tracking-normal"/>
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={()=>setEditing(false)} className="btn-ghost text-sm">Cancel</button>
              <button onClick={saveEdit} disabled={busy} data-testid="vm-save-btn"
                      className="btn-primary text-sm flex items-center gap-2">
                {busy ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}
                Save
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <div><div className="overline">Paid to date</div><div className="font-serif text-xl mt-1 text-emerald-700">{AED(paid)}</div></div>
              <div><div className="overline">Pending</div><div className="font-serif text-xl mt-1 text-amber-700">{AED(pending)}</div></div>
              <div><div className="overline">Invoices</div><div className="font-serif text-xl mt-1">{invoices.length}</div></div>
              <div><div className="overline">Rating</div><div className="font-serif text-xl mt-1 flex items-center gap-1"><Star size={14} fill="currentColor" className="text-amber-500"/>{(v.rating||0).toFixed(1)}</div></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 text-sm">
              <div className="flex items-center gap-2"><Mail size={14} className="text-slate-400"/> {v.email}</div>
              <div className="flex items-center gap-2"><Phone size={14} className="text-slate-400"/> {v.phone || '—'}</div>
              {v.trn && <div className="font-mono text-xs">TRN {v.trn}</div>}
              {v.trade_license && <div className="font-mono text-xs">Licence {v.trade_license}</div>}
              {v.iban && <div className="font-mono text-xs md:col-span-2">IBAN {v.iban}</div>}
              {v.notes && <div className="md:col-span-2 text-slate-600 italic">{v.notes}</div>}
            </div>
          </>
        )}

        <div className="mb-6">
          <div className="overline mb-2 flex items-center gap-2"><FileText size={12}/> Invoices ({invoices.length})</div>
          {invoices.length === 0 ? <div className="text-sm text-slate-400">No invoices.</div> : (
            <div className="space-y-1">
              {invoices.map(i => (
                <button key={i.id} onClick={() => onOpenInvoice?.(i.id)} data-testid={`vm-invoice-${i.id}`}
                        className="w-full text-left flex items-center justify-between p-2 bg-white/60 hover:bg-white rounded-xl text-sm">
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-xs text-slate-500">{i.invoice_number}</div>
                    <div className="truncate">{i.description}</div>
                  </div>
                  <div className="text-right">
                    <div>{AED(i.amount_aed)}</div>
                    <span className={`status-pill text-xs ${i.status === 'paid' ? 'status-success' : i.status === 'rejected' ? 'status-danger' : 'status-warning'}`}>{i.status?.replace(/_/g,' ')}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {boqs.length > 0 && (
          <div>
            <div className="overline mb-2 flex items-center gap-2"><Building size={12}/> BOQs ({boqs.length})</div>
            {boqs.map(b => (
              <div key={b.id} className="p-2 bg-white/60 rounded-xl text-sm mb-1 flex justify-between">
                <div>{b.title}</div>
                <div className="font-medium">{AED(b.total_aed)}</div>
              </div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
}
